import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CandidateDrugComponent } from 'app/captures/components/candidate-drug/candidate-drug.component';
import { ConsultNoteHeaderComponent } from 'app/consult-notes/components/consult-note-header/consult-note-header.component';
import { ConsultNoteTitleComponent } from 'app/consult-notes/components/consult-note-title/consult-note-title.component';
import { CaptureQueueResult } from 'app/core/models/capture-lists/capture-queue-result.model';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { NavigationService } from 'app/core/services/navigation.service';
import { PagedVerifiedCaptures, PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { HighValueIndicatorComponent } from 'app/shared/components/high-value-indicator/high-value-indicator.component';
import { PaginationComponent } from 'app/shared/components/pagination/pagination.component';
import { PrescriberNameComponent } from 'app/shared/components/prescriber-name/prescriber-name.component';
import { UserPresenceComponent } from 'app/shared/components/user-presence/user-presence.component';
import { CaptureStatusReasonPipe } from 'app/shared/pipes/capture-status-reason.pipe';
import { CaptureStatusPipe } from 'app/shared/pipes/capture-status.pipe';

@Component({
  selector: 'app-consult-note-captures',
  templateUrl: './consult-note-captures.component.html',
  styleUrl: './consult-note-captures.component.scss',
  imports: [
    ConsultNoteHeaderComponent,
    ConsultNoteTitleComponent,
    MatProgressSpinner,
    RouterLink,
    UserPresenceComponent,
    PrescriberNameComponent,
    HighValueIndicatorComponent,
    CandidateDrugComponent,
    DatePipe,
    CaptureStatusPipe,
    CaptureStatusReasonPipe,
    PaginationComponent,
  ],
})
export class ConsultNoteCapturesComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private titleService = inject(Title)
  private navigationService = inject(NavigationService);
  private userPresenceService = inject(UserPresenceService);

  patientAttachmentId: string;
  patientAttachment: PatientAttachment;
  verifiedCaptures: CaptureQueueResult[];
  paging = LimitOffsetPaging.empty;
  refreshing = true;
  loadingVerifiedCaptures = true;

  constructor() {
    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  ngOnInit(): void {
    this.patientAttachmentId = this.route.snapshot.paramMap.get('id');
    this.loadPatientAttachment(this.patientAttachmentId);
    this.loadVerifiedCaptures(this.patientAttachmentId);
    this.userPresenceService.subscribeAndJoin("consultNote", this.patientAttachmentId);
  }

  ngOnDestroy(): void {
    this.userPresenceService.leaveAndUnsubscribe("consultNote", this.patientAttachmentId);
  }

  onPageChange(page: number) {
    this.paging.currentPage = page;
    this.loadVerifiedCaptures(this.patientAttachmentId);
  }

  private loadPatientAttachment(patientAttachmentId): void {
    this.refreshing = true;
    this.patientAttachmentsService.get(patientAttachmentId).subscribe({
      next: (patientAttachment: PatientAttachment) => {
        this.refreshing = false;
        this.patientAttachment = patientAttachment;
        this.setTitle();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.navigationService.notFound();
        } else {
          console.error(err);
        }
      }
    });

    window.scrollTo(0, 0);
  }

  private loadVerifiedCaptures(patientAttachmentId): void {
    this.patientAttachmentsService.getVerifiedCapturesList(patientAttachmentId,
      this.paging.currentPage, this.paging.pageSize).subscribe({
        next: (results: PagedVerifiedCaptures) => {
          this.verifiedCaptures = results.captures;
          this.paging = results.meta.paging;
        }
      })
  }

  private setTitle(): void {
    const title = `NuvemRx 340B Recovery - ${this.patientAttachment.id} | Verified Captures`;
    this.titleService.setTitle(title);
  }

  private handleRouteChange(paramsMap): void {
    const params = paramsMap['params'];
    const patientAttachmentId = params && params['id'];

    if (this.navigationService.shouldReloadPage(this.patientAttachment, patientAttachmentId)) {
      this.loadPatientAttachment(patientAttachmentId);
    }
  }
}
